// @flow
/** @jsx jsx */

import { Component } from 'react';

import Icon from '../Icon';
import { Colors } from '@zapier/theme';
import { keyCodes } from '@zapier/common-utils';
import { jsx, keyframes } from '@emotion/core';

const transition = 'left 300ms ease-out';

const toggleStyles = ({ isDisabled, isSmall }) => ({
  borderRadius: '50px',
  cursor: isDisabled ? 'default' : 'pointer',
  display: 'inline-block',
  overflow: 'hidden',
  position: 'relative',
  height: isSmall ? '30px' : '32px',
  opacity: isDisabled && '0.5',
  verticalAlign: 'middle',
  pointerEvents: isDisabled && 'none',
  width: isSmall ? '74px' : '80px',
});

const pulseKeyframes = keyframes({
  '0%': {
    opacity: 1.0,
  },
  '50%': {
    opacity: 0.4,
  },
  '100%': {
    opacity: 1.0,
  },
});

const togglingDotStyles = isToggling =>
  isToggling
    ? {
        animation: `${pulseKeyframes} 1.5s ease-out infinite`,
      }
    : {};

const dotStyles = ({ isOn, isSmall, isToggling }) => ({
  position: 'absolute',
  height: 16,
  width: 16,
  backgroundColor: Colors.white,
  borderRadius: '50%',
  top: '7px',
  left: isOn ? (isSmall ? 51 : 57) : 7, // refactor this later on
  transition,
  zIndex: 1,
  ...togglingDotStyles(isToggling),
});

const iconStyles = ({ isOn, isSmall }) => ({
  color: Colors.white,
  position: 'absolute',
  top: '7px',
  left: isOn ? (isSmall ? 51 : 57) : 9, // refactor this later on
  transition,
  zIndex: 1,
});

const commonButtonStyles = {
  height: '100%',
  lineHeight: '30px',
  position: 'absolute',
  transition,
  width: '80px',
};

const togglingButtonStyles = isToggling =>
  isToggling
    ? {
        cursor: 'wait',
      }
    : {};

const buttonStyles = ({ isOn, isSmall, isToggling }) => ({
  borderRadius: '50px',
  color: Colors.white,
  height: '32px',
  fontSize: isSmall ? '13px' : '14px',
  fontWeight: 600,
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  ':disabled': {
    color: Colors.white,
  },
  ':hover': {
    color: Colors.white,
  },
  ':before': {
    ...commonButtonStyles,
    backgroundColor: Colors.viper,
    content: '"ON"',
    left: isOn ? '0' : '-80px',
    textAlign: 'left',
    paddingLeft: isSmall ? '10px' : '8px',
    top: 0,
  },
  ':after': {
    ...commonButtonStyles,
    backgroundColor: Colors.steel,
    content: '"OFF"',
    left: isOn ? '80px' : '0px',
    paddingLeft: '35px',
    top: 0,
  },
  ...togglingButtonStyles(isToggling),
});

type Props = {
  isDisabled?: boolean,
  isOn: boolean,
  isSmall?: boolean,
  isToggling?: boolean,
  onToggle: (itemIdToggled: boolean) => void,
  showIcon?: boolean,
};

type State = {
  isOn: boolean,
};

class OnOffToggle extends Component<Props, State> {
  static defaultProps = {
    isOn: true,
  };

  state = {
    isOn: this.props.isOn,
  };

  static getDerivedStateFromProps(nextProps: Props, state: State) {
    if (nextProps.isOn !== state.isOn) {
      return {
        isOn: nextProps.isOn,
      };
    }

    return null;
  }

  handleKeyPress = (e: SyntheticKeyboardEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const {
      // $FlowFixMe: Flow doesn't like this. property keyCode is missing in event[1]
      nativeEvent: { keyCode },
    } = e;

    if (keyCode === keyCodes.Enter || keyCode === keyCodes.Space) {
      this.handleClick();
    }
  };

  handleClick = () => {
    if (this.props.isDisabled || this.props.isToggling) {
      return;
    }

    this.setState(
      prevState => ({ isOn: !prevState.isOn }),
      this.propagateToggle
    );
  };

  propagateToggle = () => {
    this.props.onToggle(this.state.isOn);
  };

  render() {
    const { isOn } = this.state;
    const { isDisabled, isSmall, isToggling, showIcon } = this.props;

    return (
      <div
        css={toggleStyles({ isDisabled, isSmall })}
        disabled={isDisabled || isToggling}
      >
        <button
          aria-disabled={isDisabled || isToggling}
          aria-label="On off switch"
          aria-pressed={isOn}
          checked={isOn}
          css={buttonStyles({ isOn, isSmall, isToggling })}
          data-switch={true}
          data-switch-toggle={true}
          data-switch-toggle-pressed={isOn}
          disabled={isDisabled}
          onClick={this.handleClick}
          onKeyPress={this.handleKeyPress}
        >
          {showIcon ? (
            <span aria-hidden={true} css={iconStyles({ isOn, isSmall })}>
              <Icon alt="" name="eye" />
            </span>
          ) : (
            <span
              aria-hidden={true}
              css={dotStyles({ isOn, isSmall, isToggling })}
            />
          )}
        </button>
      </div>
    );
  }
}

export default OnOffToggle;
